import React from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { useCalendarLocationIds } from '../../../stores/calendar-location.store';
import { useCalendarStore } from '../../../stores/calendar.store';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { useZoomSafeContext } from './zoom-context';

interface ZoomControlsProps {
  className?: string;
}

const trackingIds = CALENDAR_TRACKING_IDS.zoomControls;

export const ZoomControls: React.FC<ZoomControlsProps> = ({ className }) => {
  const { t } = useTranslation('schedule');
  const { zoomIn, zoomOut, canZoomIn, canZoomOut } = useZoomSafeContext();
  const { selectedOrgId } = useAppScopeStore();
  const { calendarEntityType } = useCalendarStore(['calendarEntityType']);
  const { calendarLocationIds } = useCalendarLocationIds();

  const user = getUser();

  const handleCollapseClick = () => {
    zoomIn();
    SchedulerV3.trackPendoEvent('schedule_calendar_collapse_button_clicked', {
      organizationId: selectedOrgId,
      locationIds: calendarLocationIds,
      scheduleView: calendarEntityType,
      userId: user?.userID || '',
    });
  };

  const handleExpandClick = () => {
    zoomOut();
    SchedulerV3.trackPendoEvent('schedule_calendar_expand_button_clicked', {
      organizationId: selectedOrgId,
      locationIds: calendarLocationIds,
      scheduleView: calendarEntityType,
      userId: user?.userID || '',
    });
  };

  return (
    <div className={className} css={containerStyle}>
      <Button
        variant='secondary'
        iconName='collapse-small'
        hoverLabel={t('Collapse view')}
        onClick={handleCollapseClick}
        disabled={canZoomIn}
        trackingId={trackingIds.collapseButton}
      />
      <Button
        variant='secondary'
        iconName='expand-small'
        hoverLabel={t('Expand view')}
        onClick={handleExpandClick}
        disabled={canZoomOut}
        trackingId={trackingIds.expandButton}
      />
    </div>
  );
};

const containerStyle = css({
  position: 'absolute',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  'button, button[aria-disabled="true"]': {
    background: theme.colors.white,
    border: `1px solid ${theme.colors.neutral20}`,
  },
});
